<template>
  <div class="employee-dashboard" :class="{ 'nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <div class="dashboard-content">
      <div class="page-container">
        <div class="modern-page-header">
          <div class="header-content">
            <div class="title-section">
              <h1 class="dashboard-title">My Training</h1>
              <p class="dashboard-subtitle">
                <span class="icon">📚</span>
                Your certifications, training history, and official training report submissions
              </p>
            </div>
            <div class="header-actions">
              <button class="refresh-btn" @click="loadData" :disabled="loading">
                <i class="bx bx-refresh" :class="{ 'spin': loading }"></i> Refresh
              </button>
            </div>
          </div>
          <div class="breadcrumb-section">
          </div>
        </div>

        <div class="metrics-grid">
          <div class="metric-card gradient-1">
            <div class="metric-icon"><i class="bx bx-certification"></i></div>
            <div class="metric-content">
              <p class="metric-label">My Certifications</p>
              <h2 class="metric-value">{{ certifications.length }}</h2>
            </div>
          </div>
          <div class="metric-card gradient-2">
            <div class="metric-icon"><i class="bx bx-check-circle"></i></div>
            <div class="metric-content">
              <p class="metric-label">Verified</p>
              <h2 class="metric-value">{{ verifiedCount }}</h2>
            </div>
          </div>
          <div class="metric-card gradient-3">
            <div class="metric-icon"><i class="bx bx-book"></i></div>
            <div class="metric-content">
              <p class="metric-label">Scheduled for me</p>
              <h2 class="metric-value">{{ scheduledTrainings.length }}</h2>
            </div>
          </div>
          <div class="metric-card gradient-4">
            <div class="metric-icon"><i class="bx bx-file-blank"></i></div>
            <div class="metric-content">
              <p class="metric-label">Training reports submitted</p>
              <h2 class="metric-value">{{ myReports.length }}</h2>
            </div>
          </div>
        </div>

        <div class="report-launch-wrap hr-mt-4">
          <button type="button" class="btn-submit-training-blink" @click="openReportForm">
            Submit Training Report
          </button>
          <button
            v-if="showReportForm"
            type="button"
            class="refresh-btn ghost-close"
            @click="showReportForm = false"
          >
            Close form
          </button>
        </div>

        <div v-show="showReportForm" ref="reportFormPanel" class="hr-chart-card hr-mt-4">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-file"></i> Training report form</h5>
          </div>
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-2">
              Complete the county <strong>Training Report Form</strong>. You may submit for <strong>training you already attended</strong> (retrospective) or recent activity—use the actual dates you were in session.
            </p>
            <p class="hr-mb-3">
              <a class="hr-link-pdf" :href="formPdfUrl" target="_blank" rel="noopener">
                <i class="bx bx-download"></i> Download blank form (PDF)
              </a>
            </p>
            <label class="tr-field tr-span-2 tr-check-row">
              <span><input v-model="reportForm.is_past_training" type="checkbox" /> This report is for training I have already completed (retrospective)</span>
            </label>
            <div v-if="targetsError" class="hr-alert hr-alert-warning">{{ targetsError }}</div>
            <form class="tr-form" @submit.prevent="submitTrainingReport">
              <div class="tr-grid">
                <label class="tr-field tr-span-2">
                  <span>To be submitted to <span class="req">*</span></span>
                  <select v-model.number="reportForm.submit_target_id" class="tr-input" required>
                    <option :value="0" disabled>Select recipient</option>
                    <option v-for="t in submitTargets" :key="t.id" :value="t.id">{{ t.label }}</option>
                  </select>
                </label>
                <label class="tr-field">
                  <span>Officer name</span>
                  <input v-model="reportForm.officer_name" type="text" class="tr-input" placeholder="Auto-filled from profile if empty">
                </label>
                <label class="tr-field">
                  <span>Designation</span>
                  <input v-model="reportForm.designation" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Department / section</span>
                  <input v-model="reportForm.department_section" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Station</span>
                  <input v-model="reportForm.station" type="text" class="tr-input">
                </label>
                <label class="tr-field tr-span-2">
                  <span>Title of training / workshop / seminar <span class="req">*</span></span>
                  <input v-model="reportForm.training_title" type="text" class="tr-input" required>
                </label>
                <label class="tr-field">
                  <span>Training institution / organizer</span>
                  <input v-model="reportForm.institution_organizer" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Venue</span>
                  <input v-model="reportForm.venue" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Duration from</span>
                  <input v-model="reportForm.date_from" type="date" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Duration to</span>
                  <input v-model="reportForm.date_to" type="date" class="tr-input">
                </label>
                <label class="tr-field tr-span-2">
                  <span>Sponsoring agency (if any)</span>
                  <input v-model="reportForm.sponsoring_agency" type="text" class="tr-input">
                </label>
                <label class="tr-field tr-span-2">
                  <span>Type of training</span>
                  <input v-model="reportForm.training_type" type="text" class="tr-input" placeholder="e.g. Local · Workshop">
                </label>
                <label class="tr-field tr-span-2">
                  <span>Objectives of the training</span>
                  <textarea v-model="reportForm.objectives" class="tr-textarea" rows="3"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Summary of training content</span>
                  <textarea v-model="reportForm.summary_content" class="tr-textarea" rows="3"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Key learnings and skills acquired</span>
                  <textarea v-model="reportForm.key_learnings" class="tr-textarea" rows="3"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Application / relevance to work</span>
                  <textarea v-model="reportForm.application_relevance" class="tr-textarea" rows="3"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Challenges / lessons learned</span>
                  <textarea v-model="reportForm.challenges" class="tr-textarea" rows="2"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Recommendations</span>
                  <textarea v-model="reportForm.recommendations" class="tr-textarea" rows="2"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Attachments (describe what you will attach / have attached)</span>
                  <input v-model="reportForm.attachments_note" type="text" class="tr-input" placeholder="Invitation, certificate, materials…">
                </label>
                <label class="tr-field">
                  <span><input type="checkbox" v-model="reportForm.attachment_invitation"> Copy of Training Invitation / Nomination Letter</span>
                </label>
                <label class="tr-field">
                  <span><input type="checkbox" v-model="reportForm.attachment_certificate"> Copy of Certificate of Participation / Completion</span>
                </label>
                <label class="tr-field tr-span-2">
                  <span><input type="checkbox" v-model="reportForm.attachment_materials"> Training Materials / Program attached</span>
                </label>

                <div class="tr-section-title tr-span-2">Supporting uploads (optional)</div>
                <p class="hr-text-muted tr-span-2 hr-mb-1" style="grid-column: span 2">
                  PDF or images up to 10 MB each. HR can view these from <strong>Training Management</strong>.
                </p>
                <label class="tr-field tr-span-2">
                  <span>Invitation / nomination letter</span>
                  <input ref="docInvitation" type="file" class="tr-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                </label>
                <label class="tr-field tr-span-2">
                  <span>Certificate of participation</span>
                  <input ref="docCertificate" type="file" class="tr-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                </label>
                <label class="tr-field tr-span-2">
                  <span>Training materials / program</span>
                  <input ref="docMaterials" type="file" class="tr-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                </label>

                <div class="tr-section-title tr-span-2">J. Officer's Declaration</div>
                <label class="tr-field">
                  <span>Officer signature/name</span>
                  <input v-model="reportForm.officer_signature" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Date</span>
                  <input v-model="reportForm.officer_declaration_date" type="date" class="tr-input">
                </label>

                <div class="tr-section-title tr-span-2">K. Supervisor's Comments</div>
                <label class="tr-field tr-span-2">
                  <span>Comments on relevance of training to duties/performance</span>
                  <textarea v-model="reportForm.supervisor_comments" class="tr-textarea" rows="3"></textarea>
                </label>
                <label class="tr-field">
                  <span>Name of supervisor</span>
                  <input v-model="reportForm.supervisor_name" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Supervisor designation</span>
                  <input v-model="reportForm.supervisor_designation" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Supervisor signature</span>
                  <input v-model="reportForm.supervisor_signature" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Supervisor date</span>
                  <input v-model="reportForm.supervisor_date" type="date" class="tr-input">
                </label>

                <div class="tr-section-title tr-span-2">L. Directorate of HRM & Development (Official Use)</div>
                <label class="tr-field">
                  <span>Received by</span>
                  <input v-model="reportForm.hr_received_by" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Date received</span>
                  <input v-model="reportForm.hr_received_date" type="date" class="tr-input">
                </label>
                <label class="tr-field tr-span-2">
                  <span>Remarks</span>
                  <textarea v-model="reportForm.hr_remarks" class="tr-textarea" rows="2"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Action taken</span>
                  <textarea v-model="reportForm.hr_action_taken" class="tr-textarea" rows="2"></textarea>
                </label>
              </div>
              <p v-if="reportMsg" class="hr-mt-2" :class="reportOk ? 'hr-text-success' : 'hr-text-danger'">{{ reportMsg }}</p>
              <button type="submit" class="refresh-btn hr-mt-2" :disabled="reportSubmitting">
                {{ reportSubmitting ? "Submitting…" : "Submit training report" }}
              </button>
            </form>
          </div>
        </div>

        <div class="hr-chart-card hr-mt-4">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> My submitted training reports</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="myReports.length === 0" class="hr-text-center hr-py-4 hr-text-muted">No training reports submitted yet.</div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>Training title</th>
                    <th>Submitted to</th>
                    <th>Dates</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in myReports" :key="r.id">
                    <td>{{ r.created_at || "—" }}</td>
                    <td>{{ r.training_title }}</td>
                    <td>{{ r.submit_to_label }}</td>
                    <td>{{ r.date_from || "—" }} → {{ r.date_to || "—" }}</td>
                    <td><span class="hr-badge hr-badge-info">{{ r.status }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-certification"></i> My Certifications</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="loading" class="hr-loading-wrap">
              <div class="hr-spinner"></div>
              <p class="hr-text-muted">Loading...</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Certificate / Training</th>
                    <th>Issuing body</th>
                    <th>Issue date</th>
                    <th>Expiry</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, idx) in certifications" :key="c.certificateID">
                    <td>{{ idx + 1 }}</td>
                    <td>
                      {{ c.certificateName }}
                      <span v-if="c.certificateNumber" class="hr-text-muted">({{ c.certificateNumber }})</span>
                      <span v-if="c.trainingName" class="hr-small hr-d-block hr-text-muted">Training: {{ c.trainingName }}</span>
                    </td>
                    <td>{{ c.issuingBody || '—' }}</td>
                    <td>{{ c.issueDate }}</td>
                    <td>{{ c.expiryDate || '—' }}</td>
                    <td><span class="hr-badge" :class="statusClass(c.verificationStatus)">{{ c.verificationStatus }}</span></td>
                  </tr>
                  <tr v-if="certifications.length === 0">
                    <td colspan="6" class="hr-text-center hr-py-4 hr-text-muted">No certifications recorded for you yet.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="hr-chart-card hr-mt-4">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-calendar-event"></i> My scheduled trainings</h5>
          </div>
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-3">Trainings for you or your department. View details and confirm attendance.</p>
            <div v-if="scheduledTrainings.length === 0" class="hr-text-center hr-py-4 hr-text-muted">No trainings scheduled for you.</div>
            <div v-else class="training-list">
              <div v-for="s in scheduledTrainings" :key="s.schedule_id" class="training-item">
                <div class="training-item-icon"><i class="bx bx-book"></i></div>
                <div class="training-item-body">
                  <strong>{{ s.trainingName }}</strong>
                  <span class="hr-badge hr-badge-info">{{ s.trainingType }}</span>
                  <span v-if="s.acknowledged" class="hr-badge hr-badge-success hr-ml-2">Confirmed</span>
                  <p class="hr-text-muted hr-mb-1 hr-small">
                    {{ s.start_date }}<span v-if="s.end_date && s.end_date !== s.start_date"> – {{ s.end_date }}</span>
                    · {{ s.venue || 'Venue TBC' }}
                  </p>
                  <p class="hr-text-muted hr-mb-2 hr-small">Audience: {{ s.audience_label || '—' }}</p>
                  <div class="training-item-actions">
                    <button type="button" class="refresh-btn sm" @click="openScheduleDetail(s)">View details</button>
                    <button
                      v-if="!s.acknowledged"
                      type="button"
                      class="refresh-btn sm primary"
                      :disabled="ackSubmitting === s.schedule_id"
                      @click="acknowledgeSchedule(s)"
                    >
                      {{ ackSubmitting === s.schedule_id ? 'Saving…' : 'I will attend' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="detailSchedule" class="hr-modal-overlay" @click.self="detailSchedule = null">
          <div class="hr-modal detail-modal">
            <div class="hr-modal-header">
              <h3>{{ detailSchedule.trainingName }}</h3>
              <button type="button" class="hr-modal-close" @click="detailSchedule = null">&times;</button>
            </div>
            <div class="hr-p-3 detail-body">
              <p><strong>Code:</strong> {{ detailSchedule.trainingCode || '—' }}</p>
              <p><strong>Dates:</strong> {{ detailSchedule.start_date }}<span v-if="detailSchedule.end_date && detailSchedule.end_date !== detailSchedule.start_date"> – {{ detailSchedule.end_date }}</span></p>
              <p><strong>Venue:</strong> {{ detailSchedule.venue || '—' }}</p>
              <p><strong>Provider:</strong> {{ detailSchedule.provider || '—' }}</p>
              <p><strong>Type:</strong> {{ detailSchedule.trainingType || '—' }}</p>
              <p><strong>Audience:</strong> {{ detailSchedule.audience_label || '—' }}</p>
              <p v-if="detailSchedule.duration_days || detailSchedule.duration_hours"><strong>Duration:</strong> {{ detailSchedule.duration_days ? detailSchedule.duration_days + ' days' : '' }} {{ detailSchedule.duration_hours ? detailSchedule.duration_hours + ' hours' : '' }}</p>
              <p v-if="detailSchedule.description"><strong>Description:</strong> {{ detailSchedule.description }}</p>
              <p v-if="detailSchedule.learning_objectives"><strong>Objectives:</strong> {{ detailSchedule.learning_objectives }}</p>
              <p v-if="detailSchedule.prerequisites"><strong>Prerequisites:</strong> {{ detailSchedule.prerequisites }}</p>
              <p v-if="detailSchedule.notes"><strong>Session notes:</strong> {{ detailSchedule.notes }}</p>
              <p v-if="detailSchedule.acknowledged_at" class="hr-text-success"><strong>Attendance confirmed:</strong> {{ detailSchedule.acknowledged_at }}</p>
              <p v-if="ackMsg" :class="ackOk ? 'hr-text-success' : 'hr-text-danger'">{{ ackMsg }}</p>
              <button
                v-if="!detailSchedule.acknowledged"
                type="button"
                class="refresh-btn primary hr-mt-2"
                :disabled="ackSubmitting === detailSchedule.schedule_id"
                @click="acknowledgeSchedule(detailSchedule)"
              >
                {{ ackSubmitting === detailSchedule.schedule_id ? 'Saving…' : 'I confirm I will attend' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/Navigation.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "MyTraining",
  components: { Navigation },
  data() {
    return {
      isNavCollapsed: false,
      loading: false,
      certifications: [],
      scheduledTrainings: [],
      detailSchedule: null,
      ackSubmitting: 0,
      ackMsg: "",
      ackOk: false,
      submitTargets: [],
      targetsError: "",
      myReports: [],
      showReportForm: false,
      reportSubmitting: false,
      reportMsg: "",
      reportOk: false,
      reportForm: {
        submit_target_id: 0,
        is_past_training: true,
        officer_name: "",
        designation: "",
        department_section: "",
        station: "",
        training_title: "",
        institution_organizer: "",
        venue: "",
        date_from: "",
        date_to: "",
        sponsoring_agency: "",
        training_type: "",
        objectives: "",
        summary_content: "",
        key_learnings: "",
        application_relevance: "",
        challenges: "",
        recommendations: "",
        attachments_note: "",
        attachment_invitation: false,
        attachment_certificate: false,
        attachment_materials: false,
        officer_signature: "",
        officer_declaration_date: "",
        supervisor_comments: "",
        supervisor_name: "",
        supervisor_designation: "",
        supervisor_signature: "",
        supervisor_date: "",
        hr_received_by: "",
        hr_received_date: "",
        hr_remarks: "",
        hr_action_taken: ""
      }
    };
  },
  computed: {
    verifiedCount() {
      return this.certifications.filter(c => (c.verificationStatus || "").toLowerCase() === "verified").length;
    },
    formPdfUrl() {
      const base = process.env.BASE_URL || "/";
      const b = base.endsWith("/") ? base : base + "/";
      return `${b}docs/TRAINING_REPORT_FORM.pdf`;
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },
    openReportForm() {
      this.showReportForm = true;
      this.$nextTick(() => {
        const el = this.$refs.reportFormPanel;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    applyProfileToReportForm(userInfo) {
      if (userInfo.names && !this.reportForm.officer_name) {
        this.reportForm.officer_name = userInfo.names;
      }
    },
    gotTo(route) {
      const path = route.startsWith("/") ? route : "/" + route;
      this.$router.push(path);
    },
    statusClass(status) {
      const s = (status || "").toLowerCase();
      if (s === "verified") return "hr-badge-success";
      if (s === "pending") return "hr-badge-warning";
      return "hr-badge-secondary";
    },
    async loadData() {
      this.loading = true;
      const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
      const empNo = userInfo.idNo || sessionStorage.getItem("sessionIdNo") || "";

      try {
        if (empNo) {
          const certData = new FormData();
          certData.append("function", "getEmployeeCertifications");
          certData.append("empNo", empNo);
          const certRes = await execute(certData, authUrl);
          const certPayload = certRes?.data?.data;
          this.certifications = Array.isArray(certPayload?.certifications) ? certPayload.certifications : [];
        } else {
          this.certifications = [];
        }

        await this.loadScheduledTrainings();

        this.applyProfileToReportForm(userInfo);
        await this.loadSubmitTargets();
        await this.loadMyReports();
      } catch (_) {
        this.certifications = [];
        this.scheduledTrainings = [];
      } finally {
        this.loading = false;
      }
    },
    async loadSubmitTargets() {
      this.targetsError = "";
      try {
        const fd = new FormData();
        fd.append("function", "getTrainingReportSubmitTargets");
        const res = await execute(fd, authUrl);
        if (res.data?.success && res.data.data?.targets) {
          this.submitTargets = res.data.data.targets;
          if (!this.reportForm.submit_target_id && this.submitTargets.length) {
            this.reportForm.submit_target_id = 0;
          }
        } else {
          this.submitTargets = [];
          this.targetsError = res.data?.message || "Could not load submit-to options. Run DB script training_report_form_module.sql if needed.";
        }
      } catch (e) {
        this.submitTargets = [];
        this.targetsError = e?.message || "Could not load submit-to options.";
      }
    },
    async loadScheduledTrainings() {
      try {
        const fd = new FormData();
        fd.append("function", "getMyTrainingSchedules");
        const res = await execute(fd, authUrl);
        this.scheduledTrainings = res.data?.success && Array.isArray(res.data.data?.schedules)
          ? res.data.data.schedules
          : [];
      } catch (_) {
        this.scheduledTrainings = [];
      }
    },
    openScheduleDetail(s) {
      this.detailSchedule = { ...s };
      this.ackMsg = "";
    },
    async acknowledgeSchedule(s) {
      const id = s.schedule_id;
      this.ackSubmitting = id;
      this.ackMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "acknowledgeTrainingSchedule");
        fd.append("scheduleID", id);
        const res = await execute(fd, authUrl);
        this.ackOk = !!res.data?.success;
        this.ackMsg = res.data?.message || (this.ackOk ? "Confirmed." : "Could not confirm.");
        if (this.ackOk) {
          await this.loadScheduledTrainings();
          if (this.detailSchedule && this.detailSchedule.schedule_id === id) {
            const updated = this.scheduledTrainings.find(x => x.schedule_id === id);
            if (updated) this.detailSchedule = { ...updated };
          }
        }
      } catch (e) {
        this.ackOk = false;
        this.ackMsg = e?.message || "Could not confirm attendance.";
      } finally {
        this.ackSubmitting = 0;
      }
    },
    async loadMyReports() {
      try {
        const fd = new FormData();
        fd.append("function", "listMyTrainingReports");
        const res = await execute(fd, authUrl);
        this.myReports = res.data?.success && Array.isArray(res.data.data?.reports) ? res.data.data.reports : [];
      } catch (_) {
        this.myReports = [];
      }
    },
    async submitTrainingReport() {
      if (!this.reportForm.training_title.trim()) {
        this.reportOk = false;
        this.reportMsg = "Training title is required.";
        return;
      }
      if (!this.reportForm.submit_target_id) {
        this.reportOk = false;
        this.reportMsg = "Select where the report is to be submitted.";
        return;
      }
      this.reportSubmitting = true;
      this.reportMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "submitMyTrainingReport");
        fd.append("is_past_training", this.reportForm.is_past_training ? "1" : "0");
        Object.keys(this.reportForm).forEach((k) => {
          if (k === "is_past_training") return;
          const v = this.reportForm[k];
          if (typeof v === "boolean") {
            fd.append(k, v ? "1" : "0");
          } else {
            fd.append(k, v == null || v === "" ? "" : String(v));
          }
        });
        const inv = this.$refs.docInvitation?.files?.[0];
        const cert = this.$refs.docCertificate?.files?.[0];
        const mat = this.$refs.docMaterials?.files?.[0];
        if (inv) fd.append("training_doc_invitation", inv);
        if (cert) fd.append("training_doc_certificate", cert);
        if (mat) fd.append("training_doc_materials", mat);
        const res = await execute(fd, authUrl);
        this.reportOk = !!res.data?.success;
        this.reportMsg = res.data?.message || (this.reportOk ? "Submitted." : "Submit failed.");
        if (this.reportOk) {
          this.showReportForm = false;
          const keep = {
            officer_name: this.reportForm.officer_name,
            designation: this.reportForm.designation,
            department_section: this.reportForm.department_section,
            station: this.reportForm.station
          };
          this.reportForm = {
            submit_target_id: 0,
            is_past_training: true,
            officer_name: keep.officer_name,
            designation: keep.designation,
            department_section: keep.department_section,
            station: keep.station,
            training_title: "",
            institution_organizer: "",
            venue: "",
            date_from: "",
            date_to: "",
            sponsoring_agency: "",
            training_type: "",
            objectives: "",
            summary_content: "",
            key_learnings: "",
            application_relevance: "",
            challenges: "",
            recommendations: "",
            attachments_note: "",
            attachment_invitation: false,
            attachment_certificate: false,
            attachment_materials: false,
            officer_signature: "",
            officer_declaration_date: "",
            supervisor_comments: "",
            supervisor_name: "",
            supervisor_designation: "",
            supervisor_signature: "",
            supervisor_date: "",
            hr_received_by: "",
            hr_received_date: "",
            hr_remarks: "",
            hr_action_taken: ""
          };
          if (this.$refs.docInvitation) this.$refs.docInvitation.value = "";
          if (this.$refs.docCertificate) this.$refs.docCertificate.value = "";
          if (this.$refs.docMaterials) this.$refs.docMaterials.value = "";
          await this.loadMyReports();
        }
      } catch (e) {
        this.reportOk = false;
        this.reportMsg = e?.message || "Submit failed.";
      } finally {
        this.reportSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.metrics-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 20px; margin-bottom: 24px; }
.metric-card { display: flex; align-items: center; gap: 16px; padding: 20px; border-radius: 12px; background: #fff; border: 1px solid #e2e8f0; }
.metric-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 24px; color: #fff; }
.metric-card.gradient-1 .metric-icon { background: #059669; }
.metric-card.gradient-2 .metric-icon { background: #0ea5e9; }
.metric-card.gradient-3 .metric-icon { background: #8b5cf6; }
.metric-card.gradient-4 .metric-icon { background: #0f766e; }
.tr-form { max-width: 960px; }
.tr-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
@media (max-width: 768px) { .tr-grid { grid-template-columns: 1fr; } .tr-span-2 { grid-column: span 1; } }
.tr-span-2 { grid-column: span 2; }
.tr-field span { display: block; font-size: 0.85rem; font-weight: 600; margin-bottom: 4px; color: #334155; }
.tr-field .req { color: #dc2626; }
.tr-check-row span {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-weight: 500;
  font-size: 0.9rem;
  color: #334155;
}
.tr-check-row input[type="checkbox"] {
  margin-top: 3px;
}
.report-launch-wrap {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.btn-submit-training-blink {
  padding: 14px 28px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-weight: 800;
  font-size: 1rem;
  letter-spacing: 0.02em;
  color: #fff;
  background: linear-gradient(135deg, #059669, #047857);
  animation: blink-training-btn 1.15s ease-in-out infinite;
}
.btn-submit-training-blink:hover {
  filter: brightness(1.05);
}
@keyframes blink-training-btn {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(5, 150, 105, 0.55);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 22px 6px rgba(5, 150, 105, 0.35);
    transform: scale(1.02);
  }
}
.refresh-btn.ghost-close {
  background: #fff;
  color: #334155;
  border: 1px solid #cbd5e1;
}
.tr-section-title { font-weight: 700; font-size: 0.95rem; color: #0f172a; border-top: 1px dashed #cbd5e1; padding-top: 10px; margin-top: 6px; }
.tr-input, .tr-textarea { width: 100%; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 0.9rem; }
.tr-textarea { resize: vertical; min-height: 64px; }
.hr-link-pdf { color: #059669; font-weight: 600; text-decoration: none; }
.hr-link-pdf:hover { text-decoration: underline; }
.hr-ms-2 { margin-left: 8px; }
.hr-text-success { color: #059669; }
.hr-text-danger { color: #dc2626; }
.hr-alert { padding: 10px 12px; border-radius: 8px; font-size: 0.9rem; }
.hr-alert-warning { background: #fffbeb; border: 1px solid #fcd34d; color: #92400e; }
.hr-mt-2 { margin-top: 8px; }
code { font-size: 0.8rem; background: #f1f5f9; padding: 2px 6px; border-radius: 4px; }
.metric-label { margin: 0; font-size: 0.875rem; color: #64748b; }
.metric-value { margin: 4px 0 0; font-size: 1.5rem; font-weight: 700; }
.dashboard-content { margin-left: 280px; padding: 20px; transition: margin-left 0.3s ease; }
.employee-dashboard.nav-collapsed .dashboard-content { margin-left: 70px; }
@media (max-width: 768px) { .dashboard-content { margin-left: 0; } }
.modern-page-header { margin-bottom: 24px; }
.breadcrumb-section { margin-top: 12px; }
.breadcrumb { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 0.875rem; }
.breadcrumb-item { cursor: pointer; color: #059669; }
.breadcrumb-current { color: #64748b; }
.refresh-btn { padding: 8px 16px; border-radius: 8px; background: #059669; color: #fff; border: none; cursor: pointer; }
.refresh-btn:disabled { opacity: 0.7; cursor: not-allowed; }
.bx-spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.training-list { display: flex; flex-direction: column; gap: 12px; }
.training-item { display: flex; align-items: flex-start; gap: 16px; padding: 12px 16px; background: #f8fafc; border-radius: 12px; border: 1px solid #e2e8f0; }
.training-item-icon { width: 44px; height: 44px; border-radius: 10px; background: #059669; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 20px; }
.training-item-body { flex: 1; }
.hr-small { font-size: 0.875rem; }
.hr-d-block { display: block; }
.hr-ml-2 { margin-left: 8px; }
.hr-mb-1 { margin-bottom: 4px; }
.hr-mb-2 { margin-bottom: 8px; }
.hr-mb-3 { margin-bottom: 12px; }
.training-item-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.refresh-btn.sm { padding: 6px 12px; font-size: 0.85rem; background: #fff; color: #334155; border: 1px solid #cbd5e1; }
.refresh-btn.sm.primary { background: #059669; color: #fff; border-color: #059669; }
.hr-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
}
.detail-modal {
  background: #fff;
  border-radius: 16px;
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}
.hr-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}
.hr-modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #64748b;
}
.hr-p-3 { padding: 20px; }
.detail-body p { margin-bottom: 8px; }
</style>
